import React from 'react'
import './index.scss'
import { ICONS } from '@Const/common'
import clsx from 'clsx'
import { ZodFormattedError } from 'zod'
import { stringEscapeNewlines } from '@App/helpers/string-escape-newlines'
import { Maybe } from '@Types'
import { WorkType } from '../work-type'
import { RightsholderContact } from '@Entities/rightsholder/rightsholder-contact'
import { tableCellSanitizer } from '@App/helpers/table-cell-sanitizer'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import PublisherWriterSection from '@Components/ViewWork/PublisherCard/PublisherWriterSection'
import { joinErrors } from '@App/helpers/join-errors'
import PublisherContactSection from '@Components/ViewWork/PublisherCard/PublisherContactSection'

const isOriginalPublisher = (publisher: OriginalPublisherModel | PublisherModel): publisher is OriginalPublisherModel =>
    (publisher as OriginalPublisherModel).administratorPublishers !== undefined

const getPublisherHeaders = (isOp: boolean) => {
    if (isOp) {
        return {
            nameHeader: 'Publisher Name',
            publisherNumberHeader: 'Publisher Number',
            ipiHeader: 'Publisher IPI',
            writersHeader: 'Represented Writers',
        }
    } else {
        return {
            nameHeader: 'Administrator Name',
            publisherNumberHeader: 'Administrator Number',
            ipiHeader: 'Administrator IPI',
            writersHeader: '',
        }
    }
}

const getSubPublisherHeaders = (isSubPublisher: boolean) => {
    if (isSubPublisher) {
        return {
            collectionShareHeader: '',
            publicContactHeader: '',
        }
    } else {
        return {
            collectionShareHeader: 'Collection Share',
            publicContactHeader: 'Contact Details',
        }
    }
}

interface PublisherCardInterface {
    publisher: OriginalPublisherModel | PublisherModel
    publicContact?: Maybe<RightsholderContact>
    errors?: ZodFormattedError<OriginalPublisherModel> | ZodFormattedError<PublisherModel>
    isExpanded?: boolean
    toggleExpanded?: () => void
    isSubPublisher?: boolean
    workType?: WorkType
}

const PublisherCard = ({
    isSubPublisher,
    isExpanded,
    errors,
    toggleExpanded,
    publisher,
    publicContact,
    workType,
}: PublisherCardInterface) => {
    const isOp = isOriginalPublisher(publisher)
    let originalPub
    let originalError: ZodFormattedError<OriginalPublisherModel> | undefined
    if (isOp) {
        originalPub = publisher as OriginalPublisherModel
        originalError = errors as ZodFormattedError<OriginalPublisherModel>
    }

    const { nameHeader, publisherNumberHeader, ipiHeader, writersHeader } = getPublisherHeaders(isOp)
    const { collectionShareHeader, publicContactHeader } = getSubPublisherHeaders(!!isSubPublisher)
    const displayContact = workType === 'work' || workType === 'overclaim'
    const chevron = isExpanded ? ICONS.chevron_drop_up : ICONS.chevron_drop_down

    return (
        <div className={clsx('publisher-card', isExpanded && 'expanded')}>
            <div className={clsx('card-header', !displayContact && 'no-contact')}>
                <>
                    <div>{nameHeader}</div>
                    <div>{ipiHeader}</div>
                    <div>{publisherNumberHeader}</div>
                    <div>{writersHeader}</div>
                    <div>{collectionShareHeader}</div>
                    {displayContact && <div>{publicContactHeader}</div>}
                </>
            </div>
            <div className={clsx('card-body primary', !displayContact && 'no-contact')}>
                <div className="hide-header">
                    {!isOp && <img src={chevron} alt="expand" onClick={toggleExpanded} />}
                </div>
                <div data-label={nameHeader}>
                    <span>{publisher.publisherName && stringEscapeNewlines(publisher.publisherName)}</span>
                    <span className="error-field">{joinErrors(errors?.publisherName?._errors)}</span>
                </div>
                <div data-label={ipiHeader}>
                    <span>{tableCellSanitizer(publisher.ipiNumber)}</span>
                    <span className="error-field">{joinErrors(errors?.ipiNumber?._errors)}</span>
                </div>
                <div data-label={publisherNumberHeader}>
                    <span>{tableCellSanitizer(publisher.hfaPublisherNumber)}</span>
                </div>
                <div data-label={writersHeader}>
                    <PublisherWriterSection isOp={isOp} originalPub={originalPub} originalError={originalError} />
                </div>
                <div
                    className={clsx('publisher-share', !collectionShareHeader && 'hide-header')}
                    data-label={collectionShareHeader}
                >
                    <span>{!isSubPublisher && publisher.publisherShare + '%'}</span>
                    <span className="error-field">
                        {!isSubPublisher && joinErrors(errors?.publisherShare?._errors)}
                    </span>
                </div>
                <PublisherContactSection
                    displayContact={displayContact}
                    publicContactHeader={!!publicContactHeader}
                    isSubPublisher={isSubPublisher}
                    publicContact={publicContact}
                />
            </div>
        </div>
    )
}

export default PublisherCard
